import { ReactNode } from "react";
import { useForm } from "react-hook-form";
import Modal from "../../../layouts/Modal";
import useEbay from "../../../data/useEbay";
import useEbayCategories from "../../../data/useEbayCategories";
import SelectInput from "../../shared/Inputs/SelectInput";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import useNotification from "../../notifications/useNotifications";

export interface IBulkUpdatePolicies {
  locationKey?: string;
  paymentPolicyId?: string;
  fulfillmentPolicyId?: string;
  returnPolicyId?: string;
  storeCategoryId?: string;
}

interface IBulkUpdatePoliciesModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (policiesToUpdate: IBulkUpdatePolicies) => void;
  isLoading: boolean;
  title?: string;
  description?: string;
  submitLabel?: string;
  showLocation?: boolean;
  /** Only for a caller whose submit sends storeCategoryId on to the policy update queue. */
  showStoreCategory?: boolean;
  /** Extra controls under the policy selects, for a caller that needs an option of its own. */
  children?: ReactNode;
}

export default function BulkUpdatePoliciesModal({
  open,
  onClose,
  onSubmit,
  isLoading,
  title = "Bulk Update eBay Listing Policies",
  description = "Select the policies you want to update for the selected parts. Leave fields empty to keep existing values.",
  submitLabel = "Update Policies",
  showLocation = true,
  showStoreCategory = false,
  children,
}: IBulkUpdatePoliciesModalProps) {
  const { addNotification } = useNotification();
  // Mounted on every listings page; only read from eBay once the modal is actually shown.
  const { policies, locations } = useEbay({ enabled: open, locations: showLocation });
  const { storeCategories } = useEbayCategories({ categories: false, storeCategories: showStoreCategory, enabled: open });

  // A plain select rather than EbayStoreCategoryDropdown: that one fills in the first shop category
  // when empty, which here would move every selected listing. Empty has to mean "keep what it has".
  const storeCategoryOptions = (storeCategories.data ?? []).map((x) => ({ id: x.categoryId, name: x.path ?? x.categoryName }));

  const { register, setValue, watch, handleSubmit, reset } = useForm<IBulkUpdatePolicies>({
    defaultValues: {
      locationKey: undefined,
      paymentPolicyId: undefined,
      fulfillmentPolicyId: undefined,
      returnPolicyId: undefined,
      storeCategoryId: undefined,
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFormSubmit = (data: IBulkUpdatePolicies) => {
    const policiesToUpdate: IBulkUpdatePolicies = {};
    if (data.locationKey) policiesToUpdate.locationKey = data.locationKey;
    if (data.paymentPolicyId) policiesToUpdate.paymentPolicyId = data.paymentPolicyId;
    if (data.fulfillmentPolicyId) policiesToUpdate.fulfillmentPolicyId = data.fulfillmentPolicyId;
    if (data.returnPolicyId) policiesToUpdate.returnPolicyId = data.returnPolicyId;
    if (showStoreCategory && data.storeCategoryId) policiesToUpdate.storeCategoryId = data.storeCategoryId;

    if (Object.keys(policiesToUpdate).length === 0) {
      addNotification({ primaryText: showStoreCategory ? "Please select at least one policy or store category to update" : "Please select at least one policy to update", variant: "warn" });
      return;
    }

    onSubmit(policiesToUpdate);
    reset();
  };

  return (
    <Modal open={open} setOpen={handleClose} width="max-w-5xl">
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="space-y-4">
          <h2 className="text-lg font-medium leading-6 text-gray-900">{title}</h2>
          <p className="text-sm text-gray-600">
            {description}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {showLocation && (
              <SelectInput label={"Location of Part"} register={register} registerName={"locationKey"} options={locations.data?.data.locations || []} setValue={setValue} watch={watch} />
            )}
            <SelectInput label={"Shipping Policy"} register={register} registerName={"fulfillmentPolicyId"} options={policies.data?.data.fulfilmentPolicies || []} setValue={setValue} watch={watch} />
            <SelectInput label={"Payment Policy"} register={register} registerName={"paymentPolicyId"} options={policies.data?.data.paymentPolicies || []} setValue={setValue} watch={watch} />
            <SelectInput label={"Returns Policy"} register={register} registerName={"returnPolicyId"} options={policies.data?.data.returnPolicies || []} setValue={setValue} watch={watch} />
            {showStoreCategory && (
              <SelectInput label={"Store Category"} register={register} registerName={"storeCategoryId"} options={storeCategoryOptions} setValue={setValue} watch={watch} />
            )}
          </div>
          {children}
          <div className="flex justify-end gap-4 pt-4 border-t">
            <ButtonInput label={"Cancel"} isSubmit={false} onClick={handleClose} classes={"!bg-gray-200 hover:!bg-gray-300 !text-gray-700"} />
            <ButtonInput label={submitLabel} isSubmit={true} onClick={undefined} classes={undefined} isLoading={isLoading} />
          </div>
        </div>
      </form>
    </Modal>
  );
}
